import { RouterModule, Routes } from '@angular/router';

import { LoginComponent }          from './core/login';
import { ForgotPasswordComponent } from './core/forgot-password';
import { ResetPasswordComponent }  from './core/reset-password';
import { TemporaryLoginComponent } from './core/temporary-login';
import { PageNotFoundComponent }   from './core/page-not-found';
import { HelpComponent }           from './core/help';
import { EnvironmentInfoComponent } from './core/environment-info';

import { AuthGuard } from '@app/_guards';

import * as routes from './routes';

const appRoutes: Routes = [
  // Home (`''`) is registered first via DashboardRoutingModule (imported before
  // this forRoot config) so eagerly loaded feature modules cannot steal `/`.
  {
    path     : 'login',
    component: LoginComponent
  },
  {
    path     : 'help',
    component: HelpComponent
  },
  {
    path     : 'environment-info',
    component: EnvironmentInfoComponent,
    canActivate: [AuthGuard]
  },
  {
    path     : 'temporary-login',
    component: TemporaryLoginComponent
  },
  {
    path     : 'password',
    component: ForgotPasswordComponent
  },
  {
    path     : 'resetpassword',
    component: ResetPasswordComponent
  },
  {
    path        : 'front',
    loadChildren: () => import('./routes/front-route/front-route.module').then(m => m.FrontRouteModule)
  },
  {
    path        : 'projects/:projectId/statistics',
    loadChildren: () => import('./modules/statistics/statistics.module').then(m => m.StatisticsModule)
  },
  {
    path        : 'users',
    loadChildren: () => import('./modules/user/user.module').then(m => m.UserModule)
  },
  {
    path        : 'projects',
    loadChildren: () => import('./modules/project/project.module').then(m => m.ProjectModule)
  },
  {
    path        : 'project',
    loadChildren: () => import('./modules/project/project.module').then(m => m.ProjectModule)
  },
  {
    path        : 'projects/:projectId/influencers',
    loadChildren: () => import('./modules/influencer/influencer.module').then(m => m.InfluencerModule)
  },
  // Bookmarks for the old Templates page, which Template Management replaced.
  { path: 'system-admin/templates', pathMatch: 'full', redirectTo: 'template-management' },
  {
    path        : 'template-management',
    loadChildren: () => import('./modules/template-generator/template-management/template-management.module').then(m => m.TemplateManagementModule)
  },  
  {
    path        : 'widget-monitoring',
    loadChildren: () => import('./modules/widget-monitoring/widget-monitoring.module').then(m => m.WidgetMonitoringModule)
  },
  {
    path        : 'data-protection',
    loadChildren: () => import('./modules/data-protection/data-protection.module').then(m => m.DataProtectionModule)
  },
  {
    path        : 'projects/:id/campaigns',
    loadChildren: () => import('./modules/campaign/campaign.module').then(m => m.CampaignModule)
  },
  {
    // TODO: figure out why routes.campaignTemplates({projectId: ':projectId', campaignId: ':campaignId'})
    // doesn't work here
    path        : 'projects/:projectId/campaigns/:campaignId',
    loadChildren: () => import('./modules/template-generator/template-generator.module').then(m => m.TemplateGeneratorModule)
  },
  {
    path        : 'clients',
    loadChildren: () => import('./modules/client/client.module').then(m => m.ClientModule)
  },
  {
    path        : 'organizations',
    loadChildren: () => import('./modules/organization/organization.module').then(m => m.OrganizationModule)
  },
  {
    path        : 'organization',
    loadChildren: () => import('./modules/organization/organization.module').then(m => m.OrganizationModule)
  },
  {
    path        : 'projects/:projectId/segments',
    loadChildren: () => import('./modules/segment/segment.module').then(m => m.SegmentModule)
  },
  {
    path        : 'system-admin',
    loadChildren: () => import('./modules/system-admin/system-admin.module').then(m => m.SystemAdminModule)
  },
  {
    path: 'admin/bunny-cdn-overview',
    redirectTo: 'system-admin/cdn-monitoring',
    pathMatch: 'full'
  },
  {
    path: 'admin/cdn-monitoring',
    redirectTo: 'system-admin/cdn-monitoring',
    pathMatch: 'full'
  },
  // Bookmarks for pages moved from /settings/* to /system-admin/* by the design-system port.
  { path: 'settings', pathMatch: 'full', redirectTo: 'system-admin/settings' },
  { path: 'settings/add', pathMatch: 'full', redirectTo: 'system-admin/settings/add' },
  { path: 'settings/edit/:id', pathMatch: 'full', redirectTo: 'system-admin/settings/edit/:id' },
  { path: 'settings/cache', pathMatch: 'full', redirectTo: 'system-admin/cache' },
  { path: 'settings/global-stats', pathMatch: 'full', redirectTo: 'system-admin/global-stats' },
  { path: 'settings/rendering-system', pathMatch: 'full', redirectTo: 'system-admin/rendering-system' },
  { path: 'settings/ai-avatar-monitoring', pathMatch: 'full', redirectTo: 'system-admin/ai-avatar-monitoring' },
  { path: 'settings/task-queues', pathMatch: 'full', redirectTo: 'system-admin/task-queues' },
  { path: 'settings/social-share-monitoring', pathMatch: 'full', redirectTo: 'system-admin/social-share-monitoring' },
  { path: 'settings/packages', pathMatch: 'full', redirectTo: 'system-admin/packages' },
  { path: 'settings/packages/add', pathMatch: 'full', redirectTo: 'system-admin/packages/add' },
  { path: 'settings/packages/edit/:id', pathMatch: 'full', redirectTo: 'system-admin/packages/edit/:id' },
  { path: 'service-providers', pathMatch: 'full', redirectTo: 'system-admin/service-providers' },
  { path: 'templates', pathMatch: 'full', redirectTo: 'template-management' },
  {
    path        : 'settings',
    loadChildren: () => import('./modules/settings/settings.module').then(m => m.SettingsModule)
  },
  {
    path        : 'registration-platform',
    loadChildren: () => import('./modules/registration-platform/registration-platform.module').then(m => m.RegistrationPlatformModule)
  },
  {
    path        : 'packages',
    loadChildren: () => import('./modules/registration-platform/registration-platform.module').then(m => m.RegistrationPlatformModule)
  },
  {
    path        : 'domains',
    loadChildren: () => import('./modules/mailer-domain/mailer-domain.module').then(m => m.MailerDomainModule)
  },
  {
    path        : 'api-access',
    loadChildren: () => import('./modules/api-access/api-access.module').then(m => m.ApiAccessModule)
  },
  // Old links to where these pages used to live. The /users/integration redirect lives in
  // the user routing module, which owns that path.
  {
    path        : 'api-keys',
    redirectTo  : 'api-access',
    pathMatch   : 'full'
  },
  {
    path        : 'api-keys/add',
    redirectTo  : 'api-access/add',
    pathMatch   : 'full'
  },
  {
    path        : 'system-admin/api-keys',
    redirectTo  : 'api-access',
    pathMatch   : 'full'
  },
  {
    path        : 'system-admin/api-keys/add',
    redirectTo  : 'api-access/add',
    pathMatch   : 'full'
  },
  {
    path        : 'influencer-sso',
    loadChildren: () => import('./modules/influencer-sso/influencer-sso.module').then(m => m.InfluencerSsoModule)
  },
  {
    path        : 'global-widgets',
    loadChildren: () => import('./modules/global-widget/global-widget.module').then(m => m.GlobalWidgetModule)
  },
  {
    path        : 'system-admin/service-providers',
    loadChildren: () => import('./modules/mailer-service-provider/mailer-service-provider.module').then(m => m.MailerServiceProviderModule)
  },
  {
    path        : 'logs',
    loadChildren: () => import('./modules/logs/logs.module').then(m => m.LogsModule)
  },
  {
    path        : 'onboarding-form',
    loadChildren: () => import('./modules/onboarding-form/onboarding-form.module').then(m => m.OnboardingFormModule)
  },
  {
    path     : '**',
    component: PageNotFoundComponent
  }
];

export const routing = RouterModule.forRoot (
  appRoutes,
  {
    useHash: false,
    scrollPositionRestoration: 'enabled'
  }
);
